import type { LatLng } from "./places.js";

export type LngLatCoord = [number, number];

export interface RoutePathMeta {
  distance_m?: number;
  duration_s?: number;
  provider?: string;
  provider_leg_id?: string;
  sample_count?: number;
}

/**
 * One routable path variant (e.g. primary vs alternative). Adapters fill **`coordinates`** as
 * `[[lng, lat], …]` — the canonical geometry for maps and Turf. The optional **`line`** field exists
 * only for API shapes that already provide GeoJSON `LineString` / `MultiLineString` without a flat
 * coordinate ring (e.g. some persisted `route_options` from device-api). **Do not** set both
 * redundant copies of the same `LineString`; prefer **`coordinates`** only when you have a simple line.
 */
export interface RoutePathOption {
  id?: string;
  /** UI-only display name (e.g. learned API `name`). Not a persistence key. */
  label?: string;
  coordinates?: LngLatCoord[];
  line?: {
    type: "LineString" | "MultiLineString";
    coordinates: unknown;
  };
  meta?: RoutePathMeta;
}

/**
 * One directed leg of the itinerary: from stop `fromStopIndex` to `toStopIndex` (inclusive indices of
 * the `stops[]` array passed to {@link RoutingProvider.getRoute}).
 */
export interface RouteSegmentPlan {
  sequence: number;
  fromStopIndex: number;
  toStopIndex: number;
  /** Several ways to travel this leg (e.g. Mapbox/Google alternatives). */
  paths: RoutePathOption[];
}

/**
 * Full multi-leg answer: **ordered segments** (A→B, B→C, …), each with **path** options and metrics.
 * This is the domain DTO — not a vendor response. Map to device-api `route_options` when persisting.
 */
export interface RoutePlan {
  segments: RouteSegmentPlan[];
  meta?: RoutePlanMeta;
}

/** Top-level metadata for a {@link RoutePlan} (provider id, timing — not persisted to device-api). */
export interface RoutePlanMeta {
  provider?: string;
  /** Echo of {@link RoutingRequestOptions.transportProfile} when the adapter applied it. */
  transportProfile?: RoutingTransportProfile;
  /** Set by adapters when the HTTP call fails (truncated body or message). */
  error?: string;
  /**
   * Wall-clock ms spent decoding encoded polylines into coordinates (e.g. Google Routes
   * `encodedPolyline` → `[lng, lat][]`). Adapter-only; not persisted to device-api.
   */
  polylineDecodeMs?: number;
  /** Sum of decoded vertex counts across all polylines (for rough cost per point). */
  polylineDecodeVertexCount?: number;
}

/**
 * How to route. Drives Mapbox **profile** (`driving` vs `driving-traffic`, etc.) and Google
 * `travelMode` / `routingPreference`. Neither Google Routes nor Mapbox Directions exposes a dedicated
 * HGV/truck mode — car routing only (`DRIVE` / `driving` / `driving-traffic`); see provider JSDoc for links.
 */
export type RoutingTransportProfile =
  | "driving-traffic"
  | "driving"
  | "walking"
  | "cycling";

/** Default for {@link RoutingRequestOptions.transportProfile}. */
export const DEFAULT_ROUTING_TRANSPORT_PROFILE: RoutingTransportProfile =
  "driving-traffic";

export interface RoutingRequestOptions {
  signal?: AbortSignal;
  /**
   * Default `driving-traffic` (live traffic where the vendor supports it).
   * Use `driving` to prefer topology without traffic (Google `TRAFFIC_UNAWARE`; Mapbox `driving` profile).
   */
  transportProfile?: RoutingTransportProfile;
}

export interface RoutingProvider {
  getRoute(
    stops: LatLng[],
    options?: RoutingRequestOptions,
  ): Promise<RoutePlan>;
}
